import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { Package, MapPin, Trash, CheckCircle, ArrowSquareOut, Warning } from "@phosphor-icons/react";

/**
 * AdminLeftBehind — portfolio-wide table of every logged left-behind
 * item across every unit. Use this before turnovers to make sure the
 * crew hauls everything before the next tenant walks in.
 */
export default function AdminLeftBehind() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open"); // open | resolved | all
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/left-behind", { params });
      setItems(r.data?.items || []);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/left-behind/${id}`, { status });
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/left-behind/${id}`);
      setItems(items.filter(x => x.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  // Group by property → then by unit for cleaner scanning
  const byAddress = {};
  items.forEach(it => {
    const addr = it.address || "(no address)";
    if (!byAddress[addr]) byAddress[addr] = {};
    if (!byAddress[addr][it.unit]) byAddress[addr][it.unit] = [];
    byAddress[addr][it.unit].push(it);
  });
  const addresses = Object.keys(byAddress).sort();
  const totalOpen = items.filter(i => i.status === "open").length;

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="left-behind-page">
        <div className="flex items-center gap-3 flex-wrap">
          <Package weight="fill" size={30} className="text-amber-600"/>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight">Items Left Behind</h1>
          <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-amber-500 text-slate-900"
            data-testid="lb-total-open">{totalOpen} open</span>
        </div>
        <div className="mt-1 text-sm text-slate-600">
          Every piece of previous-tenant junk still sitting in a unit. Log it once and the move-in crew (and the tenant page + project page) all get warned automatically.
        </div>

        <div className="mt-4 flex gap-1" data-testid="lb-filters">
          {["open", "resolved", "all"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              data-testid={`lb-filter-${f}`}
              className={`h-9 px-3 font-black uppercase tracking-widest text-[10px] border-2 ${
                filter === f
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"
              }`}>{f}</button>
          ))}
        </div>

        {loading && <div className="mt-6 text-slate-500">Loading…</div>}

        {!loading && addresses.length === 0 && (
          <div className="mt-8 p-8 text-center bg-slate-50 border-2 border-dashed border-slate-300">
            <div className="text-slate-500">No {filter === "all" ? "" : filter} items logged.</div>
            <div className="mt-1 text-[11px] text-slate-400">
              Add items from a unit's detail page or a project page.
            </div>
          </div>
        )}

        {addresses.map(addr => {
          const units = byAddress[addr];
          const unitKeys = Object.keys(units).sort();
          const addressOpen = Object.values(units).flat().filter(i => i.status === "open").length;
          return (
            <div key={addr} className="mt-6" data-testid={`lb-address-${addr}`}>
              <div className="flex items-center gap-2 pb-1 border-b-2 border-slate-900">
                <MapPin weight="fill" size={16} className="text-slate-700"/>
                <Link to={`/admin/properties/${encodeURIComponent(addr)}`}
                  className="font-display font-black text-lg uppercase tracking-tight hover:text-emerald-600">
                  {addr}
                </Link>
                {addressOpen > 0 && (
                  <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-red-600 text-white">
                    {addressOpen} open
                  </span>
                )}
              </div>
              {unitKeys.map(u => (
                <div key={u} className="mt-3">
                  <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1">
                    Unit {u} · {units[u].length} item{units[u].length === 1 ? "" : "s"}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {units[u].map(it => (
                      <LeftBehindCard key={it.id} item={it} address={addr}
                        onResolve={() => setStatus(it.id, it.status === "open" ? "resolved" : "open")}
                        onDelete={() => remove(it.id)}/>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </AdminShell>
  );
}

function LeftBehindCard({ item, address, onResolve, onDelete }) {
  const isOpen = item.status === "open";
  return (
    <div className={`p-2 border-2 ${isOpen ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-slate-50 opacity-70"}`}
      data-testid={`lb-card-${item.id}`}>
      <div className="flex items-start gap-2">
        {item.photo_path ? (
          <a href={fileUrl(item.photo_path)} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <img src={fileUrl(item.photo_path)} alt="" className="h-16 w-16 object-cover border border-slate-400"/>
          </a>
        ) : (
          <div className="h-16 w-16 bg-slate-200 border border-slate-400 flex items-center justify-center text-slate-400 text-[9px] shrink-0 text-center px-1">
            No photo
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-slate-900 break-words">{item.description}</div>
          <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
            {(item.created_at || "").slice(0, 10)} · {item.created_by || "—"}
          </div>
        </div>
      </div>
      <div className="mt-2 flex gap-1">
        <Link to={`/admin/properties/${encodeURIComponent(address)}/unit/${encodeURIComponent(item.unit)}`}
          data-testid={`lb-open-unit-${item.id}`}
          className="h-8 px-2 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
          <ArrowSquareOut size={10}/> Unit
        </Link>
        <button onClick={onResolve}
          data-testid={`lb-resolve-${item.id}`}
          className={`h-8 px-2 flex-1 font-black uppercase tracking-widest text-[9px] inline-flex items-center justify-center gap-1 ${
            isOpen
              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
              : "bg-white border-2 border-slate-400 text-slate-600 hover:border-slate-900"
          }`}>
          <CheckCircle weight="fill" size={10}/> {isOpen ? "Mark resolved" : "Reopen"}
        </button>
        <button onClick={onDelete}
          data-testid={`lb-delete-${item.id}`}
          className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center shrink-0">
          <Trash size={11}/>
        </button>
      </div>
    </div>
  );
}
